/**
 * VIB-1202: Revidering av godkjent hovedbudsjett.
 *
 * Ren logikk (ingen React/Supabase) for (a) vernet rundt reduksjon av timer og
 * sletting av linjer med historikk, og (b) bygging av diff-payloaden som
 * `budget_revised`-eventet logger. Testbar isolert.
 *
 * Vernet er klient-side validering FØR mutasjonene kjøres — det erstatter ikke
 * DB-låsene (`assert_project_writable` for completed-prosjekter,
 * `enforce_month_lock_on_time_entries` for timeføringer), men fanger de
 * revisjons-spesifikke tilfellene (redusere under loggede/fullførte timer,
 * røre en låst måned, slette en linje med loggede timer) med tydelige meldinger
 * før noe skrives.
 */

export interface BudgetLineSnapshot {
  id: string;
  description: string | null;
  phase: string | null;
  cost_type: string | null;
  quantity: number | null;
  quantity_hours: number | null;
  amount: number | null;
  estimated_cost: number | null;
}

/** Vern-relevant kontekst for én budsjettlinje. */
export interface LineGuardContext {
  /** Σ loggede timer (time_entries) på linjen. */
  loggedHours: number;
  /** Σ assignment.hours med task_status IN ('completed','approved'). */
  completedApprovedHours: number;
  /**
   * Har linjen minst én assignment med task_status IN ('completed','approved')
   * som ble fullført i en LÅST måned (bruker har rad i monthly_confirmations
   * for completed_at-årets/månedens (user_id, year, month))?
   */
  hasLockedMonthCompletion: boolean;
}

export type HoursChangeVerdict = "ok" | "needs_confirmation" | "blocked_month_lock";

/**
 * Vurder en foreslått endring av `quantity_hours` på en eksisterende linje.
 *
 * - Rører endringen en linje med en fullført/godkjent tildeling i en LÅST
 *   måned → `blocked_month_lock` (retroaktiv faktureringsgrad-endring i en
 *   bekreftet måned er ikke tillatt). Gjelder enhver endring av timetallet,
 *   ikke bare reduksjon.
 * - Reduksjon under «gulvet» (maks av loggede timer og fullført/godkjent
 *   tildelte timer) i en ULÅST måned → `needs_confirmation` (PL må bekrefte,
 *   påvirker faktureringsgrad retroaktivt).
 * - Ellers → `ok`.
 */
export function evaluateHoursChange(
  proposedHours: number,
  guard: LineGuardContext,
): HoursChangeVerdict {
  const floor = Math.max(guard.loggedHours, guard.completedApprovedHours);
  const isReductionBelowFloor = proposedHours < floor;

  if (guard.hasLockedMonthCompletion && isReductionBelowFloor) {
    return "blocked_month_lock";
  }
  if (isReductionBelowFloor) {
    return "needs_confirmation";
  }
  return "ok";
}

export type DeletionVerdict =
  | "ok"
  | "blocked_logged_hours"
  | "blocked_completed_assignments"
  | "blocked_month_lock";

/**
 * Sletting av en linje blokkeres når den har historikk som revisjon aldri skal
 * ødelegge. `budget_line_assignments.budget_line_id` er `ON DELETE CASCADE`, så
 * en sletting river også med seg tildelingene — derfor er det IKKE nok å bare
 * sjekke loggede timer:
 *
 * - Loggede timer (`time_entries`) → blokkert (timene mister koblingen; string-
 *   match-fallbacken kan ikke gjenskape den).
 * - Fullført/godkjent-tildelte timer > 0 → blokkert selv om linjen ser «tom» ut
 *   (ingen `time_entries`): CASCADE ville slette en tildeling som teller i
 *   faktureringsgrad → retroaktiv endring.
 * - Fullført tildeling i en attestert (låst) måned → blokkert (samme
 *   månedslås-vern som for timeendring).
 *
 * PL må omfordele/nullstille tildelingene manuelt først.
 */
export function evaluateDeletion(guard: LineGuardContext): DeletionVerdict {
  if (guard.hasLockedMonthCompletion) return "blocked_month_lock";
  if (guard.loggedHours > 0) return "blocked_logged_hours";
  if (guard.completedApprovedHours > 0) return "blocked_completed_assignments";
  return "ok";
}

export type DescriptionChangeVerdict = "ok" | "needs_confirmation";

/**
 * `time_entries` kobles til budsjettlinjer via FK + string-match-fallback på
 * `description`. Endres beskrivelsen på en linje med loggede timer, kan
 * string-match-koblingen miste sporet (repo-felle). Krev derfor eksplisitt
 * bekreftelse (begrunnelses-dialogen) ved description-endring på linjer med
 * loggede timer.
 */
export function evaluateDescriptionChange(
  guard: Pick<LineGuardContext, "loggedHours">,
): DescriptionChangeVerdict {
  return guard.loggedHours > 0 ? "needs_confirmation" : "ok";
}

export type RevisionChangeKind = "update" | "create" | "delete";

export interface RevisionLineChange {
  kind: RevisionChangeKind;
  /** Linjens id (null for `create` før innsetting). */
  lineId: string | null;
  description: string | null;
  /** Endrede felter (gammel → ny). Kun felter som faktisk endret seg. */
  fields: Record<string, { old: unknown; new: unknown }>;
}

const DIFFABLE_FIELDS: Array<keyof BudgetLineSnapshot> = [
  "description",
  "phase",
  "cost_type",
  "quantity",
  "quantity_hours",
  "amount",
  "estimated_cost",
];

/** Bygg endrings-liste (kun felter som faktisk endret seg) for update-linjer. */
export function diffLine(
  before: BudgetLineSnapshot,
  after: BudgetLineSnapshot,
): RevisionLineChange | null {
  const fields: RevisionLineChange["fields"] = {};
  for (const key of DIFFABLE_FIELDS) {
    if (before[key] !== after[key]) {
      fields[key] = { old: before[key], new: after[key] };
    }
  }
  if (Object.keys(fields).length === 0) return null;
  return {
    kind: "update",
    lineId: after.id,
    description: after.description ?? before.description,
    fields,
  };
}

export interface RevisionDiffSummary {
  changes: RevisionLineChange[];
  createdCount: number;
  updatedCount: number;
  deletedCount: number;
}

export interface RevisionDraft {
  updates: Array<{ before: BudgetLineSnapshot; after: BudgetLineSnapshot }>;
  creates: Array<{ description: string | null }>;
  deletes: Array<{ before: BudgetLineSnapshot }>;
}

/** Sett sammen hele revisjonens diff-oppsummering (for event-payload + UI). */
export function buildRevisionSummary(draft: RevisionDraft): RevisionDiffSummary {
  const changes: RevisionLineChange[] = [];

  for (const u of draft.updates) {
    const d = diffLine(u.before, u.after);
    if (d) changes.push(d);
  }
  for (const c of draft.creates) {
    changes.push({ kind: "create", lineId: null, description: c.description, fields: {} });
  }
  for (const del of draft.deletes) {
    changes.push({
      kind: "delete",
      lineId: del.before.id,
      description: del.before.description,
      fields: {},
    });
  }

  return {
    changes,
    createdCount: changes.filter((c) => c.kind === "create").length,
    updatedCount: changes.filter((c) => c.kind === "update").length,
    deletedCount: changes.filter((c) => c.kind === "delete").length,
  };
}
